import { AssetFilter } from '../../types/position'
import type {
  PerformanceCalendarDay,
  PerformanceCalendarResponse,
} from '../../types/performanceCalendar'

export const DEMO_MONTHS = ['2026-08', '2026-09'] as const
export const DEMO_PORTFOLIOS = [
  { id: 'all', name: 'All Portfolios' },
  { id: 'core', name: 'Core' },
  { id: 'wheel', name: 'Wheel' },
] as const
export type DemoPortfolioId = (typeof DEMO_PORTFOLIOS)[number]['id']

// Fictional changes in account equity, in cents. There are no external cash
// flows in this example. These are illustrative inputs, not market results.
const MOVEMENTS = {
  core: {
    opening: 4_968_500,
    months: [
      [
        8500, -6240, 9550, -13730, 11200, -3420, 6785, 0, -8150, 12480, 7460, -5520, 9320, -11940,
        4880, -3700, 8075, -6300, 11950, -8270, 8570,
      ],
      [
        10235, -7510, 6330, -18725, 14260, 8900, -11240, 4500, 0, 9635, -6430, 17250, -13370, 8065,
        3540, -9215, 12880, -5860, 10920, -4210, 34282,
      ],
    ],
  },
  wheel: {
    opening: 2_514_000,
    months: [
      [
        -4270, 6150, -3880, 5240, -7420, 2310, -4980, 0, 6320, -5140, 3170, -6710, 4260, -3790,
        5840, -2610, 3450, -4180, 2790, -5620, -4930,
      ],
      [
        5370, -8260, 3145, -9990, 4820, 6415, -7350, 2150, 0, 4660, -5340, 7420, -8315, 3780, 1650,
        -4980, 5730, -4720, 3840, -3120, -8705,
      ],
    ],
  },
} as const

export function demoDateLabel(date: string, options: Intl.DateTimeFormatOptions): string {
  // A calendar date is not a browser-local timestamp. Keep its day stable in
  // every visitor timezone, just as the production calendar does.
  return new Intl.DateTimeFormat('en-US', { ...options, timeZone: 'UTC' }).format(
    new Date(`${date}T12:00:00Z`)
  )
}

export function demoMoney(value: number | null, compact = false): string {
  if (value === null) return '—'
  const amount = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: compact ? 0 : 2,
    maximumFractionDigits: compact ? (Math.abs(value) < 10 ? 2 : 0) : 2,
  }).format(Math.abs(value))
  return `${value === 0 ? '' : value < 0 ? '−' : '+'}${amount}`
}

export function demoPercent(value: number | null): string {
  return value === null
    ? '—'
    : `${value === 0 ? '' : value < 0 ? '−' : '+'}${Math.abs(value).toFixed(2)}%`
}

const emptyAssets = () => ({
  stocks: { pnl: null, contribution_pct: null },
  options: { pnl: null, contribution_pct: null },
  cash_other: { pnl: null, contribution_pct: null },
})

function tradingDate(date: string): boolean {
  const weekday = new Date(`${date}T12:00:00Z`).getUTCDay()
  // August has no US equity-market holiday; September 7 is Labor Day.
  return weekday !== 0 && weekday !== 6 && date !== '2026-09-07'
}

function monthDates(month: string): string[] {
  const [year, number] = month.split('-').map(Number)
  return Array.from(
    { length: new Date(Date.UTC(year, number, 0)).getUTCDate() },
    (_, i) => `${month}-${String(i + 1).padStart(2, '0')}`
  )
}

function portfolioDays(id: 'core' | 'wheel'): PerformanceCalendarDay[][] {
  const fixture = MOVEMENTS[id]
  const name = DEMO_PORTFOLIOS.find((portfolio) => portfolio.id === id)!.name
  let equity = fixture.opening
  return DEMO_MONTHS.map((month, monthIndex) => {
    let session = 0
    return monthDates(month).map((date) => {
      const isTradingDay = tradingDate(date)
      const opening = equity
      const movement = isTradingDay ? fixture.months[monthIndex][session++] : 0
      equity += movement
      const pnl = movement / 100
      const returnPct = (movement / opening) * 100
      return {
        date,
        status: 'final',
        is_trading_day: isTradingDay,
        pnl,
        return_pct: returnPct,
        total_pnl: pnl,
        total_return_pct: returnPct,
        opening_value: opening / 100,
        closing_value: equity / 100,
        external_flow: 0,
        // Reliable total account equity does not require a per-asset split.
        assets: emptyAssets(),
        portfolios: [
          {
            portfolio_id: id,
            name,
            pnl,
            return_pct: returnPct,
            total_pnl: pnl,
            total_return_pct: returnPct,
            status: 'final',
          },
        ],
      }
    })
  })
}

/** Combine only valued portfolios, preserving an unavailable result as null. */
export function aggregateDemoDays(days: PerformanceCalendarDay[]): PerformanceCalendarDay {
  const included = days.filter((day) => day.pnl !== null)
  const sum = (field: 'pnl' | 'opening_value' | 'closing_value' | 'external_flow') =>
    included.length === 0 || included.some((day) => day[field] === null)
      ? null
      : included.reduce((total, day) => total + Math.round(day[field]! * 100), 0) / 100
  const pnl = sum('pnl')
  const opening = sum('opening_value')
  const returnPct = pnl !== null && opening !== null && opening > 0 ? (pnl / opening) * 100 : null
  const partial = included.length > 0 && included.length < days.length
  return {
    ...days[0],
    status: included.length ? 'final' : 'missing',
    pnl,
    return_pct: returnPct,
    total_pnl: pnl,
    total_return_pct: returnPct,
    opening_value: opening,
    closing_value: sum('closing_value'),
    external_flow: sum('external_flow'),
    partial_portfolios: partial,
    data_note: partial
      ? {
          kind: 'partial_portfolios',
          portfolio_count: days.length - included.length,
          message: 'This result includes only portfolios with available data.',
        }
      : null,
    portfolios: days.flatMap((day) => day.portfolios),
  }
}

function calendar(month: string, days: PerformanceCalendarDay[]): PerformanceCalendarResponse {
  const opening = days[0].opening_value!
  const closing = days[days.length - 1].closing_value!
  // With no external flows and a complete fixed scope, production's Modified
  // Dietz monthly calculation reduces to boundary equity change / opening
  // equity. Never add daily percentage returns to get the monthly return.
  const pnl = (Math.round(closing * 100) - Math.round(opening * 100)) / 100
  return {
    month,
    today: '2026-10-01',
    timezone: 'America/New_York',
    currency: 'USD',
    asset_filter: AssetFilter.ALL,
    days,
    summary: {
      pnl,
      return_pct: (pnl / opening) * 100,
      status: 'complete',
      available_days: days.length,
      missing_days: 0,
      positive_days: days.filter((day) => day.pnl !== null && day.pnl > 0).length,
      negative_days: days.filter((day) => day.pnl !== null && day.pnl < 0).length,
      period_start: days[0].date,
      period_end: days[days.length - 1].date,
    },
  }
}

const core = portfolioDays('core')
const wheel = portfolioDays('wheel')
const CALENDARS = Object.fromEntries(
  DEMO_MONTHS.map((month, index) => [
    month,
    {
      core: calendar(month, core[index]),
      wheel: calendar(month, wheel[index]),
      all: calendar(
        month,
        core[index].map((day, dayIndex) => aggregateDemoDays([day, wheel[index][dayIndex]]))
      ),
    },
  ])
) as Record<(typeof DEMO_MONTHS)[number], Record<DemoPortfolioId, PerformanceCalendarResponse>>

/** Local fixture lookup only. Unknown months cannot fabricate more history. */
export function getDemoCalendar(
  month: string,
  portfolio: DemoPortfolioId
): PerformanceCalendarResponse | undefined {
  return CALENDARS[month as (typeof DEMO_MONTHS)[number]]?.[portfolio]
}

// A separate incomplete-day fixture keeps the default visual example clear
// while testing that missing history is never turned into a zero-dollar day.
export const UNAVAILABLE_DEMO_DAY: PerformanceCalendarDay = {
  date: '2026-09-14',
  status: 'missing',
  is_trading_day: true,
  pnl: null,
  return_pct: null,
  total_pnl: null,
  total_return_pct: null,
  external_flow: null,
  opening_value: null,
  closing_value: null,
  assets: emptyAssets(),
  portfolios: [
    {
      portfolio_id: 'core',
      name: 'Core',
      pnl: null,
      return_pct: null,
      total_pnl: null,
      total_return_pct: null,
      status: 'missing',
    },
  ],
}
